import React from 'react';
import { Center, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SiteUser } from 'clipsal-cortex-types/src/api';
import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { SwipeableSEHomeCard } from '../../../../common/components/SwipeableSEHomeCard';
import { DeleteIcon } from '../../../../styles/custom-icons';
import { trackGuestShareRemoved } from '../../../../utils/common/analytics';
import { trackDynatraceGuestShareRemoved } from '../../../../utils/native/dynatrace-events';
import { selectSite } from '../../../site/siteSlice';
import { useDeleteSiteUserMutation, useGetSiteUsersQuery } from '../../../site/siteUsersApi';
import { selectUser } from '../../../user/userSlice';
import { getAvatarColor, getAvatarInitial } from './family-member-avatar';

type Props = {
  data: SiteUser;
  index: number;
};

export function FamilyMemberCard({ data, index }: Props) {
  const { user_email: email, user_id: userId } = data;
  const { t } = useTranslation();
  const { site_id: siteId } = useSelector(selectSite);
  const { userID, role } = useSelector(selectUser);
  const [deleteUser, { isLoading: isDeleting }] = useDeleteSiteUserMutation();
  const { refetch, isLoading: isUsersLoading } = useGetSiteUsersQuery();
  const toast = useToast();
  const { isOpen: isAlertDialogOpen, onOpen: onOpenAlertDialog, onClose: onCloseAlertDialog } = useDisclosure();

  // The account owner cannot remove themselves, and admins manage members elsewhere — neither row is swipe-deletable.
  const isDeletable = userID !== userId && role !== 'ADMIN';
  const isLoading = isDeleting || isUsersLoading;

  async function handleDelete() {
    try {
      await deleteUser({ siteId, userId }).unwrap();
      await refetch().unwrap(); // wait for the list to refresh even though the mutation invalidates the query.
      trackGuestShareRemoved();
      trackDynatraceGuestShareRemoved();
      onCloseAlertDialog();
    } catch {
      toast({
        status: 'error',
        title: t('Common.something went wrong'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
      });
    }
  }

  return (
    <>
      <SwipeableSEHomeCard
        mb={3}
        width="100%"
        borderTopLeftRadius={5}
        swipeHintKey="family-members"
        hintOnTap
        disableSlide={!isDeletable}
        primaryButtonProps={{
          onClick: onOpenAlertDialog,
          'data-testid': `delete-button-${index}`,
          children: <DeleteIcon w="2rem" h="2rem" />,
        }}
      >
        <Flex alignItems="center" gap={3} data-testid={`family-member-${index}`}>
          <Center
            data-testid={`family-member-avatar-${index}`}
            flexShrink={0}
            w="36px"
            h="36px"
            borderRadius="full"
            background={getAvatarColor(email)}
            color="white"
            fontWeight={600}
          >
            {getAvatarInitial(email)}
          </Center>
          <Text isTruncated>{email}</Text>
        </Flex>
      </SwipeableSEHomeCard>

      <AlertDialogModal
        header={t('Account.remove family member')}
        subHeader=" "
        isOpen={isAlertDialogOpen}
        onClose={onCloseAlertDialog}
        confirmButtonTextColor={'customRed.500'}
        isConfirming={isLoading}
        onConfirm={handleDelete}
      />
    </>
  );
}
